import {
  closestCenter,
  defaultDropAnimation,
  MeasuringStrategy,
  type Announcements,
  type DropAnimation,
  type Modifier,
  type DragEndEvent,
  type DragMoveEvent,
  type DragOverEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import DndContext from '../dragAndDrop/DndContext';
import SortableActiveItemOverlay from '../sortableList/SortableActiveItemOverlay';
import { sortableTreeKeyboardCoordinates } from './keyboardCoordinates';
import SortableTreeDragPreview from './SortableTreeDragPreview';
import SortableTreeDragWarning from './SortableTreeDragWarning';
import SortableTreeDropIndicator from './SortableTreeDropIndicator';
import SortableTreeItem, {
  type SortableTreeDropIndicatorMeta,
  type SortableTreeItemMeta,
} from './SortableTreeItem';
import type {
  FlattenedItem,
  SensorContext,
  SortableTreeBaseContainer,
  SortableTreeBaseItem,
  SortableTreeContainer,
  SortableTreeCanDrop,
  SortableTreeMove,
  SortableTreeNode,
} from './types';
import useOptimisticTree from './useOptimisticTree';
import {
  buildNodesById,
  buildTree,
  flatten,
  getProjection,
  removeChildrenOf,
  siblingIndexOf,
} from './utils';

export type SortableTreeProps<
  TItem extends SortableTreeBaseItem,
  TContainer extends SortableTreeBaseContainer = SortableTreeBaseContainer,
> = {
  className?: string;
  /** Controlled tree. The top level holds containers only — items must be nested. */
  items: SortableTreeContainer<TItem, TContainer>[];
  /**
   * Called after a valid drop with the next tree and a {@link SortableTreeMove}
   * describing the single node that moved (its new parent and index), so the
   * move can be persisted without diffing the tree. The move is shown
   * optimistically until `items` catches up; return `false` to cancel it (the
   * tree snaps back to `items`), anything else (incl. `void`) commits.
   */
  onChange: (
    next: SortableTreeContainer<TItem, TContainer>[],
    move: SortableTreeMove<TItem, TContainer>
  ) => boolean | void;
  /**
   * Render one row. For a container this is just its header/label — its children
   * are rendered as their own indented rows, so there is no `slot`. Indentation
   * is applied by SortableTree. Render a `DragHandle` (from `@payaca/dnd`) with
   * the node's `id` inside to make the row draggable.
   */
  renderItem: (
    node: SortableTreeNode<TItem, TContainer>,
    meta: SortableTreeItemMeta<TItem, TContainer>
  ) => ReactElement;
  /**
   * The floating drag preview for the active node. Defaults to a compact chip
   * showing `getNodeLabel`; compose `SortableTreeDragPreview` to keep its frame.
   */
  renderDragOverlay?: (
    node: SortableTreeNode<TItem, TContainer>
  ) => ReactElement;
  /**
   * Renders the drop indicator shown in place of the active row while
   * dragging. Indentation is applied by SortableTree. Defaults to a line.
   */
  renderDropIndicator?: (
    meta: SortableTreeDropIndicatorMeta<TItem, TContainer>
  ) => ReactElement;
  /**
   * Decide whether the active node may drop at a candidate position. Return
   * `{ reason }` to reject it with copy that replaces the drag preview and is
   * read by screen readers. See {@link SortableTreeCanDrop}.
   */
  canDrop?: SortableTreeCanDrop<TItem, TContainer>;
  /**
   * Ids of containers that are currently collapsed — their descendants are
   * hidden from the tree (but still travel with the container on a drag).
   * Controlled: pair with {@link onToggleCollapse}. Omit both to disable
   * collapsing entirely.
   */
  collapsedIds?: UniqueIdentifier[];
  /**
   * Called when a container's collapse control is activated. The consumer owns
   * the collapsed set and toggles the id in/out of {@link collapsedIds}.
   */
  onToggleCollapse?: (id: UniqueIdentifier) => void;
  /** Pixels of indent per depth level. Defaults to 24. */
  indentationWidth?: number;
  /** Optional cap on nesting depth (unlimited by default). */
  maxDepth?: number;
  /**
   * Human-readable label for a node, used in screen-reader announcements.
   * Defaults to the node's `id` — provide this when ids are opaque.
   */
  getNodeLabel?: (node: SortableTreeNode<TItem, TContainer>) => string;
  id?: string;
};

/**
 * A nested, sortable tree of containers and items, rendered as a flat list of
 * indented rows over a single `SortableContext`. Dragging horizontally chooses
 * the depth among those valid at the current slot (a leaf can never gain
 * children, so no new groups are created). Domain drop restrictions are supplied
 * by the consumer via `canDrop`: a denied drop shows the active row as invalid
 * while dragging and is not committed. Containers can optionally be made
 * collapsible (controlled via `collapsedIds` / `onToggleCollapse`): a collapsed
 * container hides its descendants from the list while still moving them as a
 * block on a drag.
 */
const SortableTree = <
  TItem extends SortableTreeBaseItem,
  TContainer extends SortableTreeBaseContainer = SortableTreeBaseContainer,
>(
  props: SortableTreeProps<TItem, TContainer>
) => {
  const {
    className,
    items: itemsProp,
    onChange,
    renderItem,
    renderDragOverlay,
    renderDropIndicator = (meta) => <SortableTreeDropIndicator {...meta} />,
    canDrop,
    collapsedIds,
    onToggleCollapse,
    indentationWidth = 24,
    maxDepth,
    getNodeLabel = (node) => String(node.id),
    id,
  } = props;

  // Collapsing is opt-in: enabled only when the consumer supplies both the
  // controlled set and a toggle handler.
  const collapseEnabled = collapsedIds != null && onToggleCollapse != null;
  const collapsedIdSet = useMemo(() => new Set(collapsedIds), [collapsedIds]);

  // Render from the optimistic tree: a committed move shows immediately and
  // holds until `itemsProp` catches up (or the consumer cancels). Everything
  // below reads `items`, so it all operates on the displayed tree.
  const { displayItems: items, commit } = useOptimisticTree(itemsProp);

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [overId, setOverId] = useState<UniqueIdentifier | null>(null);
  const [offsetLeft, setOffsetLeft] = useState(0);
  const [currentPosition, setCurrentPosition] = useState<{
    parentId: UniqueIdentifier | null;
    overId: UniqueIdentifier;
  } | null>(null);

  // Flatten for the drag engine; strip the descendants of the active node (so a
  // container moves as a block and can't be dropped inside itself) and of any
  // collapsed container (so its subtree is hidden from the rendered list).
  const flattenedItems = useMemo(() => {
    const hiddenParentIds = [
      ...(activeId != null ? [activeId] : []),
      ...(collapseEnabled ? collapsedIdSet : []),
    ];
    return removeChildrenOf(flatten(items), hiddenParentIds);
  }, [items, activeId, collapseEnabled, collapsedIdSet]);

  const projected =
    activeId && overId
      ? getProjection(
          flattenedItems,
          activeId,
          overId,
          offsetLeft,
          indentationWidth,
          maxDepth,
          canDrop
        )
      : null;

  const sortedIds = useMemo(
    () => flattenedItems.map((node) => node.id),
    [flattenedItems]
  );

  const nodesById = useMemo(() => buildNodesById(items), [items]);

  // The keyboard coordinate getter runs outside React render, so it reads the
  // live flattened list + horizontal offset through this ref.
  const sensorContext: SensorContext<TItem, TContainer> = useRef({
    items: flattenedItems,
    offset: offsetLeft,
    canDrop,
  });
  const [coordinateGetter] = useState(() =>
    sortableTreeKeyboardCoordinates(sensorContext, indentationWidth, maxDepth)
  );
  useEffect(() => {
    sensorContext.current = {
      items: flattenedItems,
      offset: offsetLeft,
      canDrop,
    };
  }, [flattenedItems, offsetLeft, canDrop]);

  // The lifted overlay leaves the pointer over the page, where buttons and
  // inputs set their own cursors, so force the drag cursor on everything.
  useEffect(() => {
    if (activeId == null) {
      return;
    }

    document.body.classList.add(...DRAGGING_CURSOR_CLASSES);
    return () => document.body.classList.remove(...DRAGGING_CURSOR_CLASSES);
  }, [activeId]);

  const labelOf = (nodeId: UniqueIdentifier) => {
    const node = nodesById.get(nodeId);
    return node ? getNodeLabel(node) : String(nodeId);
  };

  const resetState = () => {
    setActiveId(null);
    setOverId(null);
    setOffsetLeft(0);
    setCurrentPosition(null);
  };

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveId(active.id);
    setOverId(active.id);

    const activeItem = flattenedItems.find((node) => node.id === active.id);
    if (activeItem) {
      setCurrentPosition({ parentId: activeItem.parentId, overId: active.id });
    }
  };

  const handleDragMove = ({ delta }: DragMoveEvent) => {
    setOffsetLeft(delta.x);
  };

  const handleDragOver = ({ over }: DragOverEvent) => {
    setOverId(over?.id ?? null);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    resetState();

    if (!projected || !over || !projected.isValid) {
      return;
    }

    const { depth, parentId } = projected;
    const clone = flatten(items);
    const activeIndex = clone.findIndex((node) => node.id === active.id);
    const overIndex = clone.findIndex((node) => node.id === over.id);
    const movedNode = nodesById.get(active.id);

    if (activeIndex === -1 || overIndex === -1 || !movedNode) {
      return;
    }

    clone[activeIndex] = { ...clone[activeIndex], depth, parentId };

    const movedFlat = arrayMove(clone, activeIndex, overIndex);
    const next = buildTree(movedFlat);
    // A valid drop rejects depth-0 items (an item always lands inside a
    // container), so the rebuilt root holds only containers.
    const nextContainers = next as SortableTreeContainer<TItem, TContainer>[];
    const move: SortableTreeMove<TItem, TContainer> = {
      node: movedNode,
      parentId,
      index: siblingIndexOf(movedFlat, active.id, parentId),
    };
    if (onChange(nextContainers, move) !== false) {
      commit(next);
    }
  };

  const getMovementAnnouncement = (
    eventName: 'onDragMove' | 'onDragOver' | 'onDragEnd',
    activeNodeId: UniqueIdentifier,
    overNodeId?: UniqueIdentifier
  ): string | undefined => {
    if (!overNodeId || !projected) {
      return undefined;
    }

    if (!projected.isValid) {
      const reason = projected.reason ? ` ${projected.reason}.` : '';
      return `${labelOf(activeNodeId)} can't be placed there.${reason}`;
    }

    if (eventName !== 'onDragEnd') {
      if (
        currentPosition &&
        projected.parentId === currentPosition.parentId &&
        overNodeId === currentPosition.overId
      ) {
        return undefined;
      }

      setCurrentPosition({ parentId: projected.parentId, overId: overNodeId });
    }

    const sorted = flatten(items);
    const overIndex = sorted.findIndex((node) => node.id === overNodeId);
    const activeIndex = sorted.findIndex((node) => node.id === activeNodeId);
    if (overIndex === -1 || activeIndex === -1) {
      return undefined;
    }
    const reordered = arrayMove(sorted, activeIndex, overIndex);
    const previousItem = reordered[overIndex - 1];

    const movedVerb = eventName === 'onDragEnd' ? 'dropped' : 'moved';
    const nestedVerb = eventName === 'onDragEnd' ? 'dropped' : 'nested';

    if (!previousItem) {
      const nextItem = reordered[overIndex + 1];
      return nextItem
        ? `${labelOf(activeNodeId)} was ${movedVerb} before ${labelOf(nextItem.id)}.`
        : undefined;
    }

    if (projected.depth > previousItem.depth) {
      return `${labelOf(activeNodeId)} was ${nestedVerb} under ${labelOf(previousItem.id)}.`;
    }

    let previousSibling: FlattenedItem<TItem, TContainer> | undefined =
      previousItem;
    while (previousSibling && projected.depth < previousSibling.depth) {
      const parentId: UniqueIdentifier | null = previousSibling.parentId;
      previousSibling = reordered.find((node) => node.id === parentId);
    }

    return previousSibling
      ? `${labelOf(activeNodeId)} was ${movedVerb} after ${labelOf(previousSibling.id)}.`
      : undefined;
  };

  const announcements: Announcements = {
    onDragStart({ active }) {
      return `Picked up ${labelOf(active.id)}.`;
    },
    onDragMove({ active, over }) {
      return getMovementAnnouncement('onDragMove', active.id, over?.id);
    },
    onDragOver({ active, over }) {
      return getMovementAnnouncement('onDragOver', active.id, over?.id);
    },
    onDragEnd({ active, over }) {
      return getMovementAnnouncement('onDragEnd', active.id, over?.id);
    },
    onDragCancel({ active }) {
      return `Moving was cancelled. ${labelOf(active.id)} was dropped in its original position.`;
    },
  };

  const activeNode =
    activeId != null ? (nodesById.get(activeId) ?? null) : null;

  const renderOverlayContent = (node: SortableTreeNode<TItem, TContainer>) => {
    if (projected && !projected.isValid && projected.reason) {
      return <SortableTreeDragWarning reason={projected.reason} />;
    }

    if (renderDragOverlay) {
      return renderDragOverlay(node);
    }

    return (
      <SortableTreeDragPreview
        count={
          node.type === 'container' ? flatten(node.children).length + 1 : 1
        }
      >
        <span className="truncate">{getNodeLabel(node)}</span>
      </SortableTreeDragPreview>
    );
  };

  return (
    <DndContext
      id={id}
      sortable
      keyboardCoordinateGetter={coordinateGetter}
      accessibility={{ announcements }}
      collisionDetection={closestCenter}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={resetState}
    >
      <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
        <div role="tree" className={className}>
          {flattenedItems.map((flat) => {
            const node = nodesById.get(flat.id);
            if (!node) {
              return null;
            }

            const parentNode =
              flat.parentId == null ? null : nodesById.get(flat.parentId);
            const parent = parentNode?.type === 'container' ? parentNode : null;

            const isActive = flat.id === activeId;

            // Only a container with children can collapse; `node` is the full
            // tree node (from the node map), so it still carries `children`.
            const canCollapse =
              collapseEnabled &&
              node.type === 'container' &&
              node.children.length > 0;

            return (
              <SortableTreeItem
                key={flat.id}
                node={node}
                depth={isActive && projected ? projected.depth : flat.depth}
                indentationWidth={indentationWidth}
                isInvalidDrop={
                  isActive && projected ? !projected.isValid : false
                }
                canOutdent={
                  isActive && projected
                    ? projected.validDepths.some((d) => d < projected.depth)
                    : false
                }
                canIndent={
                  isActive && projected
                    ? projected.validDepths.some((d) => d > projected.depth)
                    : false
                }
                parent={parent}
                collapsed={canCollapse && collapsedIdSet.has(node.id)}
                onToggleCollapse={
                  canCollapse ? () => onToggleCollapse?.(node.id) : undefined
                }
                renderItem={renderItem}
                renderDropIndicator={renderDropIndicator}
              />
            );
          })}
        </div>
      </SortableContext>

      <SortableActiveItemOverlay
        items={activeNode ? [activeNode] : []}
        modifiers={[liftAboveDropIndicator]}
        dropAnimation={dropAnimation}
        renderItem={(node) => (
          // Fills the overlay (sized to the active row) and pins the preview
          // to its bottom, so the lift leaves it just above the indicator.
          <div className="flex items-end">{renderOverlayContent(node)}</div>
        )}
      />
    </DndContext>
  );
};

const DRAGGING_CURSOR_CLASSES = ['cursor-move!', '**:cursor-move!'];

// Overlay modifiers only move the overlay, not its collision rect, so this
// doesn't change which slot is chosen.
const liftAboveDropIndicator: Modifier = ({ transform, draggingNodeRect }) => ({
  ...transform,
  y: transform.y - (draggingNodeRect ? draggingNodeRect.height / 2 + 4 : 0),
});

const dropAnimation: DropAnimation = {
  keyframes: ({ transform }) => [
    { opacity: 1, transform: CSS.Transform.toString(transform.initial) },
    {
      opacity: 0,
      transform: CSS.Transform.toString({
        ...transform.final,
        x: transform.final.x + 5,
        y: transform.final.y + 5,
      }),
    },
  ],
  easing: 'ease-out',
  sideEffects: ({ active }) => {
    active.node.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: defaultDropAnimation.duration,
      easing: defaultDropAnimation.easing,
    });
  },
};

export default SortableTree;
